import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Eye, Download, ChevronDown, ChevronRight, Send } from 'lucide-react';
import { Button } from '../common/ui';
import { saveBlob, blobErrorMessage } from '../../utils/downloadBlob';
import { ResolveForm, actionsFor } from './GkResolve';
import type { GkCurrency, GkFinding, GkPreview as GkPreviewData, GkPreviewDocument, GkExportResult } from './types';

/**
 * Preview of the Vasco GK receipts export (GK_EXPORT_PLAN.md 4.15, milestone M7):
 * one row per statement day document, its lines, and the exceptions that hold
 * a day. Nothing is exported or locked here.
 */

const inputClass =
  'rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500';
const labelClass = 'mb-1 text-xs font-medium text-gray-500';

export function formatCents(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '–';
  return (cents / 100).toLocaleString('sl-SI', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * A rate as stored (NUMERIC(18,6), e.g. "1.084500"): all its decimals, without
 * the trailing zeros after the 4th. A manual rate can have 6 decimals, so
 * rounding to 4 would show a different rate from the one used.
 */
export function formatRate(rate: string | null | undefined): string {
  if (rate === null || rate === undefined || rate === '') return '–';
  const match = /^(\d+)(?:\.(\d*))?$/.exec(String(rate).trim());
  if (!match) return String(rate);
  const decimals = (match[2] || '').replace(/0+$/, '').padEnd(4, '0');
  return `${match[1]}.${decimals}`;
}

function yesterdayIso(): string {
  const d = new Date(Date.now() - 24 * 3600 * 1000);
  return d.toLocaleDateString('sv-SE', { timeZone: 'Europe/Ljubljana' });
}

const LINE_TYPE_LABEL: Record<string, string> = {
  bank: 'bank',
  receivable: 'invoice',
  advance: 'advance',
  book_konto: 'resolution',
  own_transfer: 'own transfer',
  force_closed: 'force-closed link',
  credit_note: 'credit note',
  refund: 'refund',
  funding: 'refund funding',
  chargeback: 'chargeback',
  chargeback_fee: 'chargeback fee',
  fee: 'fee',
  diff: 'difference',
  fx: 'FX',
  paypal_clearing: 'PayPal clearing',
};

/** The bank statement control of a day (plan 4.9). */
const StatementCell: React.FC<{ doc: GkPreviewDocument }> = ({ doc }) => {
  const c = doc.control;
  if (!c || c.status === 'no_statement') return <span className="text-gray-400">none</span>;
  const title = `Statement: ${formatCents(c.statementCreditsCents)} (${c.statementCreditCount}); app: ${formatCents(c.appCreditsCents)} (${c.appCreditCount})`;
  if (c.status === 'ok') return <span className="text-green-700" title={title}>ok</span>;
  if (c.status === 'partial') return <span className="text-amber-700" title={title}>partial day</span>;
  return <span className="text-red-700" title={title}>differs</span>;
};

const DocumentRow: React.FC<{ doc: GkPreviewDocument; isUsd: boolean }> = ({ doc, isUsd }) => {
  const [open, setOpen] = useState(false);
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <>
      <tr className={`border-t border-gray-100 ${doc.status === 'held' ? 'bg-red-50' : ''}`}>
        <td className="py-1 pr-2">
          <button type="button" onClick={() => setOpen(!open)} className="flex items-center gap-1 text-left" aria-label={`Lines of ${doc.statementDay}`}>
            <Chevron className="h-4 w-4 text-gray-500" />
            {doc.statementDay}
          </button>
        </td>
        <td className="py-1 pr-4">{doc.dokument}</td>
        <td className="py-1 pr-4">
          {doc.status === 'ready' ? <span className="text-green-700">ready</span> : <span className="text-red-700">held</span>}
          {doc.kind !== 'normal' && <span className="ml-1 text-xs text-gray-500">({doc.kind})</span>}
        </td>
        <td className="py-1 pr-4 text-right tabular-nums">{doc.paymentIds.length}</td>
        <td className="py-1 pr-4 text-right tabular-nums">{formatCents(doc.bankCents)}</td>
        {isUsd && <td className="py-1 pr-4 text-right tabular-nums">{formatCents(doc.bankCurrencyCents)}</td>}
        {isUsd && <td className="py-1 pr-4 text-right tabular-nums">{doc.rate ? formatRate(doc.rate) : '–'}</td>}
        <td className="py-1 pr-4 text-right tabular-nums">{formatCents(doc.debitCents)}</td>
        <td className="py-1 pr-4 text-right tabular-nums">{formatCents(doc.creditCents)}</td>
        {isUsd && <td className="py-1 pr-4 text-right tabular-nums">{doc.fxCents ? formatCents(doc.fxCents) : ''}</td>}
        <td className="py-1 pr-4">
          {doc.obdobje.mesec}/{doc.obdobje.leto}
          {doc.periodMoved && <span className="ml-1 text-xs text-amber-700">moved</span>}
        </td>
        <td className="py-1 pr-4"><StatementCell doc={doc} /></td>
        <td className="py-1 pr-4 text-red-700">{doc.exceptionCodes.join(', ')}</td>
      </tr>
      {open && (
        <tr>
          <td colSpan={isUsd ? 13 : 10} className="bg-gray-50 px-6 py-2">
            {doc.lines.length === 0 ? (
              <p className="text-sm text-gray-500">No lines (no rate for this day).</p>
            ) : (
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="text-left text-gray-500">
                    <th className="py-0.5 pr-3">Konto</th>
                    <th className="py-0.5 pr-3 text-right">D</th>
                    <th className="py-0.5 pr-3 text-right">K</th>
                    <th className="py-0.5 pr-3">Partner</th>
                    <th className="py-0.5 pr-3">Veza</th>
                    <th className="py-0.5 pr-3 text-right">Valuta</th>
                    <th className="py-0.5 pr-3">Opis</th>
                    <th className="py-0.5 pr-3">Payment</th>
                    <th className="py-0.5 pr-3">Order</th>
                    <th className="py-0.5 pr-3">Type</th>
                  </tr>
                </thead>
                <tbody>
                  {doc.lines.map((l, i) => (
                    <tr key={i} className="border-t border-gray-200">
                      <td className="py-0.5 pr-3 tabular-nums">{l.konto}</td>
                      <td className="py-0.5 pr-3 text-right tabular-nums">{l.side === 'D' ? formatCents(l.amountCents) : ''}</td>
                      <td className="py-0.5 pr-3 text-right tabular-nums">{l.side === 'K' ? formatCents(l.amountCents) : ''}</td>
                      <td className="py-0.5 pr-3">{l.partnerSifra !== null ? `${l.partnerSifra} ${l.partnerName || ''}` : ''}</td>
                      <td className="py-0.5 pr-3 tabular-nums">{l.veza ? `${l.veza}/${l.letoVeza}` : ''}</td>
                      <td className="py-0.5 pr-3 text-right tabular-nums">
                        {l.currency ? `${formatCents(l.currency.amountCents)} ${l.currency.sifra === 840 ? 'USD' : 'EUR'}` : ''}
                      </td>
                      <td className="py-0.5 pr-3">{l.opis}</td>
                      <td className="py-0.5 pr-3">
                        {l.paymentId ? <Link to={`/accounting/payments/${l.paymentId}`} className="text-blue-600 hover:underline">{l.paymentId}</Link> : ''}
                      </td>
                      <td className="py-0.5 pr-3">{l.orderNumber || ''}</td>
                      <td className="py-0.5 pr-3 text-gray-500">{LINE_TYPE_LABEL[l.type] || l.type}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </td>
        </tr>
      )}
    </>
  );
};

/** Findings grouped by code, each group with its help text and items. */
export const FindingGroups: React.FC<{
  findings: GkFinding[];
  tone: 'block' | 'warning';
  canResolve?: boolean;
  onResolved?: () => void;
}> = ({ findings, tone, canResolve = false, onResolved }) => {
  const [resolving, setResolving] = useState<string | null>(null);
  const keyOf = (f: GkFinding) => `${f.code}|${f.paymentId}|${f.itemKey}`;
  const groups = useMemo(() => {
    const map = new Map<string, GkFinding[]>();
    for (const f of findings) {
      if (!map.has(f.code)) map.set(f.code, []);
      map.get(f.code)!.push(f);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b, 'en', { numeric: true }));
  }, [findings]);
  const color = tone === 'block' ? 'text-red-800' : 'text-amber-800';
  return (
    <div className="space-y-2">
      {groups.map(([code, items]) => (
        <details key={code} className="rounded-md border border-gray-200 p-2">
          <summary className={`cursor-pointer text-sm font-medium ${color}`}>
            {code} × {items.length}
            {items[0].help && <span className="ml-2 font-normal text-gray-600">{items[0].help}</span>}
          </summary>
          <ul className="mt-1 space-y-0.5 text-xs text-gray-700">
            {items.map((f, i) => (
              <li key={i}>
                {f.day && <span className="mr-2 tabular-nums text-gray-500">{f.day}</span>}
                {f.paymentId && (
                  <Link to={`/accounting/payments/${f.paymentId}`} className="mr-2 text-blue-600 hover:underline">
                    payment {f.paymentId}
                  </Link>
                )}
                {f.message}
                {canResolve && actionsFor(f).length > 0 && (
                  <button
                    type="button"
                    className="ml-2 rounded border border-blue-300 px-1.5 text-blue-700 hover:bg-blue-50"
                    onClick={() => setResolving(resolving === keyOf(f) ? null : keyOf(f))}
                  >
                    Resolve
                  </button>
                )}
                {resolving === keyOf(f) && (
                  <ResolveForm
                    finding={f}
                    onClose={() => setResolving(null)}
                    onSaved={() => {
                      setResolving(null);
                      if (onResolved) onResolved();
                    }}
                  />
                )}
              </li>
            ))}
          </ul>
        </details>
      ))}
    </div>
  );
};

const GkPreview: React.FC<{ startDate: string; enabled?: boolean; onExported?: () => void; onDecided?: () => void }> = ({
  startDate, enabled = false, onExported, onDecided,
}) => {
  const [currency, setCurrency] = useState<GkCurrency>('EUR');
  const [from, setFrom] = useState(startDate);
  const [to, setTo] = useState(yesterdayIso());
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState<GkPreviewData | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportResult, setExportResult] = useState<GkExportResult | null>(null);
  const [override, setOverride] = useState(false);
  const [overrideNote, setOverrideNote] = useState('');

  const rangeInvalid = !from || !to || from > to;
  const params = { currency, from, to };

  // Days the export would hold because the statement is missing or partial.
  const withoutStatement = (preview?.documents || []).filter(
    (d) => d.status === 'ready' && (!d.control || d.control.status === 'no_statement' || d.control.status === 'partial')
  ).length;
  // The override counts only while its checkbox is shown.
  const overrideOn = override && withoutStatement > 0;

  const handlePreview = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get<GkPreviewData>('/api/accounting/gk-export/preview', { params });
      setPreview(res.data);
      // A new preview needs a new decision about the statement control.
      setOverride(false);
      setOverrideNote('');
    } catch (err: any) {
      setPreview(null);
      setError(err?.response?.data?.error || 'Could not build the preview.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    setError('');
    try {
      const res = await axios.get('/api/accounting/gk-export/preview.xlsx', { params, responseType: 'blob' });
      saveBlob(res.data, `GK_predogled_${currency}_${from}_${to}.xlsx`);
    } catch (err: any) {
      setError(await blobErrorMessage(err, 'Could not download the preview.'));
    } finally {
      setDownloading(false);
    }
  };

  const handleExport = async () => {
    if (!preview) return;
    setExporting(true);
    setError('');
    setExportResult(null);
    try {
      const res = await axios.post<GkExportResult>('/api/accounting/gk-export/exports', {
        currency: preview.currency,
        from: preview.from,
        to: preview.to,
        overrideStatementControl: overrideOn,
        overrideNote: overrideOn ? overrideNote.trim() : null,
      });
      setExportResult(res.data);
      setOverride(false);
      setOverrideNote('');
      if (onExported) onExported();
      // Refresh first: handlePreview clears the error, and a download error must stay visible.
      await handlePreview();
      try {
        const file = await axios.get(`/api/accounting/gk-export/exports/${res.data.batchId}/file`, { responseType: 'blob' });
        saveBlob(file.data, res.data.fileName);
      } catch (err: any) {
        const message = await blobErrorMessage(err, 'The export is stored, but the download failed. Download it from the history.');
        setError((previous) => (previous ? `${previous} ${message}` : message));
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || 'The export failed.');
    } finally {
      setExporting(false);
    }
  };

  const isUsd = preview?.currency === 'USD';
  const s = preview?.summary;

  return (
    <div>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col">
          <label htmlFor="gk-currency" className={labelClass}>Currency</label>
          <select id="gk-currency" value={currency} onChange={(e) => setCurrency(e.target.value as GkCurrency)} className={inputClass}>
            <option value="EUR">EUR (symbol 33)</option>
            <option value="USD">USD (symbol 34)</option>
          </select>
        </div>
        <div className="flex flex-col">
          <label htmlFor="gk-from" className={labelClass}>From</label>
          <input id="gk-from" type="date" value={from} min={startDate} max={to || undefined} onChange={(e) => setFrom(e.target.value)} className={inputClass} />
        </div>
        <div className="flex flex-col">
          <label htmlFor="gk-to" className={labelClass}>To</label>
          <input id="gk-to" type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} className={inputClass} />
        </div>
        <Button icon={Eye} onClick={handlePreview} disabled={rangeInvalid || loading}>
          {loading ? 'Building…' : 'Preview'}
        </Button>
        <Button variant="secondary" icon={Download} onClick={handleDownload} disabled={rangeInvalid || downloading}>
          {downloading ? 'Preparing…' : 'Download xlsx preview'}
        </Button>
      </div>
      {error && <p className="mt-2 text-sm text-red-700">{error}</p>}

      {preview && s && (
        <div className="mt-4">
          {preview.rateFetch && (
            <p className={`mb-2 text-sm ${preview.rateFetch.error ? 'text-red-700' : 'text-gray-600'}`}>
              {preview.rateFetch.error
                ? `Could not fetch the Bank of Slovenia rates: ${preview.rateFetch.error}`
                : `Fetched ${preview.rateFetch.stored} Bank of Slovenia rates for the missing days.`}
            </p>
          )}
          {preview.statementFetch && (
            <p className={`mb-2 text-sm ${preview.statementFetch.error ? 'text-red-700' : 'text-gray-600'}`}>
              {preview.statementFetch.error
                ? `Could not read the bank statement from Revolut: ${preview.statementFetch.error}`
                : `Read the bank statement from Revolut for ${preview.statementFetch.days.length} days (${preview.statementFetch.newEntries} new entries).`}
            </p>
          )}
          <div className="mb-4 grid grid-cols-2 gap-3 text-sm md:grid-cols-5">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <div className="text-xs text-gray-500">Day documents</div>
              <div className="font-medium">
                <span className="text-green-700">{s.ready} ready</span>, <span className="text-red-700">{s.held} held</span>
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <div className="text-xs text-gray-500">Credits in range</div>
              <div className="font-medium tabular-nums">
                {s.paymentsInRange} · {formatCents(s.creditsCents)} {preview.currency}
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <div className="text-xs text-gray-500">Bank lines ready / held (EUR)</div>
              <div className="font-medium tabular-nums">
                {formatCents(s.bankReadyCents)} / {formatCents(s.bankHeldCents)}
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <div className="text-xs text-gray-500">Already exported payments</div>
              <div className="font-medium tabular-nums">{s.exportedPayments}</div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <div className="text-xs text-gray-500">Closed VAT period through</div>
              <div className="font-medium">{preview.settings.closedThrough || 'not set'}</div>
            </div>
          </div>

          <div className="mb-4 rounded-md border border-gray-200 p-3">
            <div className="flex flex-wrap items-center gap-3">
              <Button
                icon={Send}
                onClick={handleExport}
                disabled={!enabled || exporting || s.ready === 0 || (overrideOn && !overrideNote.trim())}
              >
                {exporting ? 'Exporting…' : `Export ${s.ready} ready day${s.ready === 1 ? '' : 's'}`}
              </Button>
              <span className="text-sm text-gray-600">
                Writes the XML file for Vasco GK and the control file for AJMP. The exported payments are locked; held days stay for a later export.
              </span>
            </div>
            {withoutStatement > 0 && (
              <div className="mt-2 text-sm">
                <p className="text-amber-700">
                  {withoutStatement} ready day{withoutStatement === 1 ? ' has' : 's have'} no complete bank statement. The export holds them unless you export without the statement control.
                </p>
                <label className="mt-1 flex items-center gap-2">
                  <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} />
                  Export without statement control
                </label>
                {override && (
                  <input
                    aria-label="Reason for exporting without statement control"
                    placeholder="Why (required, goes into the control file for AJMP)"
                    value={overrideNote}
                    onChange={(e) => setOverrideNote(e.target.value)}
                    className={`${inputClass} mt-1 w-full max-w-xl`}
                  />
                )}
              </div>
            )}
            {!enabled && <p className="mt-2 text-sm text-gray-500">The export is switched off on this server (GK_EXPORT_ENABLED).</p>}
            {exportResult && (
              <div className="mt-2 text-sm text-green-800">
                Exported {exportResult.documents.length} document{exportResult.documents.length === 1 ? '' : 's'}: {exportResult.documents.map((d) => d.dokument).join(', ')}.
                {exportResult.heldDays.length > 0 && ` Held: ${exportResult.heldDays.map((d) => `${d.day} (${d.codes.join(', ')})`).join('; ')}.`}
              </div>
            )}
          </div>

          {preview.documents.length === 0 ? (
            <p className="text-sm text-gray-500">No credits to export in this range.</p>
          ) : (
            <div className="mb-4 max-h-[32rem] overflow-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500">
                    <th className="py-1 pr-2">Statement day</th>
                    <th className="py-1 pr-4">Dokument</th>
                    <th className="py-1 pr-4">Status</th>
                    <th className="py-1 pr-4 text-right">Payments</th>
                    <th className="py-1 pr-4 text-right">Bank EUR</th>
                    {isUsd && <th className="py-1 pr-4 text-right">Bank USD</th>}
                    {isUsd && <th className="py-1 pr-4 text-right">Rate</th>}
                    <th className="py-1 pr-4 text-right">Sum D</th>
                    <th className="py-1 pr-4 text-right">Sum K</th>
                    {isUsd && <th className="py-1 pr-4 text-right">FX</th>}
                    <th className="py-1 pr-4">Period</th>
                    <th className="py-1 pr-4">Statement</th>
                    <th className="py-1 pr-4">Holds the day</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.documents.map((d) => (
                    <DocumentRow key={`${d.statementDay}-${d.dokument}`} doc={d} isUsd={isUsd} />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {preview.exceptions.length > 0 && (
            <div className="mb-4">
              <h3 className="mb-2 text-sm font-semibold text-gray-900">Exceptions that hold a day</h3>
              <FindingGroups
                findings={preview.exceptions}
                tone="block"
                canResolve={enabled}
                onResolved={() => {
                  if (onDecided) onDecided();
                  handlePreview();
                }}
              />
            </div>
          )}
          {preview.warnings.length > 0 && (
            <div className="mb-4">
              <h3 className="mb-2 text-sm font-semibold text-gray-900">Warnings</h3>
              <FindingGroups findings={preview.warnings} tone="warning" />
            </div>
          )}
          {preview.paypalBreakdowns.length > 0 && (
            <div className="mb-2">
              <h3 className="mb-1 text-sm font-semibold text-gray-900">PayPal EUR breakdowns</h3>
              <p className="mb-2 text-xs text-gray-500">
                {preview.settings.paypalEurBreakdownSymbol
                  ? `Exported on symbol ${preview.settings.paypalEurBreakdownSymbol}.`
                  : 'Preview only: AJMP has not chosen the symbol yet (question O3). The lines are in the xlsx.'}
              </p>
              <ul className="text-sm">
                {preview.paypalBreakdowns.map((b) => (
                  <li key={b.paymentId}>
                    {b.day} · payment {b.paymentId} · {b.lines.length} lines
                    {b.exceptions.length > 0 && <span className="ml-2 text-red-700">{[...new Set(b.exceptions.map((e) => e.code))].join(', ')}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GkPreview;
